import type { Player } from '@dreamteam-monorepo/ui';
import type { BEPlayerListItem, BEPlayerStatsItem } from '@/lib/network/players';
import type { BETeamPlayerDetail } from '@/lib/network/teams';

export const mapPosition = (position: string | null): Player['position'] | null => {
  if (!position) {
    return null;
  }

  if (position === 'GK' || position === 'DEF' || position === 'MID' || position === 'STR') {
    return position;
  }

  if (position === 'FWD') {
    return 'STR';
  }

  return null;
};

const toClubLabel = (shirtKey: string | null): string | undefined => {
  if (!shirtKey) {
    return undefined;
  }

  return shirtKey
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

const cleanOptionalLabel = (value: string | null | undefined): string | undefined => {
  const label = value?.trim();
  return label || undefined;
};

export const toUiPlayer = (player: BEPlayerListItem): Player | null => {
  const normalizedPosition = mapPosition(player.position);
  if (!normalizedPosition) {
    return null;
  }

  const matchdayPoints = player.matchdayPoints ?? player.gameweekPoints ?? 0;
  const contestantFlagKey =
    player.contestantFlagKey?.trim() || player.shirtKey?.trim() || undefined;

  return {
    id: player.id,
    squadId: player.squadId ?? '',
    hasLeft: player.hasLeft ?? false,
    country: contestantFlagKey,
    contestantFlagKey,
    clubName: cleanOptionalLabel(player.teamName) ?? toClubLabel(player.shirtKey),
    shirtKey: player.shirtKey ?? undefined,
    displayName: player.displayName,
    firstName: player.firstName,
    lastName: player.lastName,
    shortLastName: player.shortLastName ?? player.lastName ?? null,
    matchName: player.matchName ?? null,
    position: normalizedPosition,
    status: player.status ?? 'available',
    price: player.price,
    totalPoints: player.totalPoints,
    averagePoints: player.averagePoints,
    gameweekPoints: matchdayPoints,
    matchdayPoints,
    bonusPoints: player.bonusPoints,
    last3Average: player.last3Average,
    percentSelected: player.percentSelected,
    priceChange: player.priceChangeUp - player.priceChangeDown,
    currentPeriodFixtures: player.currentPeriodFixtures?.map(f => ({
      matchId: f.matchId,
      opponentId: f.opponentId,
      opponentName: f.opponentName,
      opponentShortName: f.opponentShortName,
      contestantFlagKey: f.contestantFlagKey,
      isHome: f.isHome,
      points: f.points,
      goalBonusNormalPoints: f.goalBonusNormalPoints,
      goals: f.goals,
      showDNP: f.showDNP,
      gameweek: f.gameweek,
      matchday: f.matchday,
      status: f.status,
      kickoffAt: f.kickoffAt,
      competitionName: f.competitionName,
    })),
    nextFixtures: player.nextFixtures?.map(f => ({
      matchId: f.fixtureId,
      opponentId: f.opponentId,
      opponentName: f.opponentName,
      opponentShortName: f.opponentShortName,
      contestantFlagKey: f.contestantFlagKey,
      isHome: f.isHome,
      points: 0,
      goalBonusNormalPoints: 0,
      gameweek: f.gameweek,
      matchday: f.matchday,
      competitionName: f.competitionName,
    })),
    suspensionDetails: player.suspensionDetails
      ? {
          type: player.suspensionDetails.type ?? '',
          startDate: player.suspensionDetails.startDate ?? '',
          endDate: player.suspensionDetails.endDate,
          matches: 0,
          description: player.suspensionDetails.description ?? '',
        }
      : null,
    injuryDetails: player.injuryDetails
      ? {
          type: player.injuryDetails.type ?? '',
          startDate: player.injuryDetails.startDate ?? '',
          endDate: player.injuryDetails.endDate,
          expectedEndDate: player.injuryDetails.expectedEndDate ?? '',
          status: player.injuryDetails.status ?? '',
        }
      : null,
  };
};

export const toUiPlayerFromTeamPlayerDetail = (player: BETeamPlayerDetail): Player | null => {
  const normalizedPosition = mapPosition(player.position);
  if (!normalizedPosition) {
    return null;
  }

  const contestantFlagKey =
    player.contestantFlagKey?.trim() || player.shirtKey?.trim() || undefined;

  return {
    id: player.id,
    squadId: player.contestantShortName ?? player.contestantName ?? '',
    hasLeft: player.hasLeft,
    country: contestantFlagKey,
    contestantFlagKey,
    clubName: cleanOptionalLabel(player.contestantName) ?? toClubLabel(player.shirtKey),
    shirtKey: player.shirtKey ?? undefined,
    displayName: player.displayName,
    firstName: player.firstName ?? undefined,
    lastName: player.lastName ?? undefined,
    shortLastName: player.shortLastName ?? player.lastName ?? null,
    matchName: player.matchName ?? null,
    position: normalizedPosition,
    status: player.availabilityDisplay ?? 'available',
    price: player.price,
    totalPoints: 0,
    averagePoints: 0,
    gameweekPoints: 0,
    matchdayPoints: 0,
    bonusPoints: 0,
    last3Average: 0,
    percentSelected: 0,
    priceChange: 0,
    suspensionDetails: null,
    injuryDetails: null,
  };
};

type UiPlayerFixture = NonNullable<Player['nextFixtures']>[number];

// The stats endpoint spells positions out in full ("Forward"), unlike the
// player-list endpoints mapPosition was written for ("FWD"), so normalise to
// the short code first or every forward silently reads as a midfielder.
const STATS_POSITION_ALIASES: Record<string, string> = {
  Goalkeeper: 'GK',
  Defender: 'DEF',
  Midfielder: 'MID',
  Forward: 'STR',
};

const mapStatsPosition = (position: string | null): Player['position'] =>
  mapPosition(position ? (STATS_POSITION_ALIASES[position] ?? position) : null) ?? 'MID';

// Maps a Stats Centre row (the stats endpoint shape) onto the UI player
// contract. Unlike the sibling mappers this never returns null: the row is
// already on screen by the time we need it, and the row itself renders an
// unmappable position as MID, so a null here would only leave the player
// modal unopenable.
export const toUiPlayerFromStatsItem = (player: BEPlayerStatsItem): Player => {
  const contestantFlagKey =
    player.contestantFlagKey?.trim() || player.shirtKey?.trim() || undefined;
  const periodPoints = player.matchdayPoints ?? player.gameweekPoints ?? 0;

  return {
    id: player.playerId,
    squadId: player.contestantId ?? '',
    hasLeft: false,
    country: contestantFlagKey,
    contestantFlagKey,
    clubName:
      cleanOptionalLabel(player.contestantName) ??
      cleanOptionalLabel(player.contestantShortName) ??
      toClubLabel(player.shirtKey),
    shirtKey: player.shirtKey ?? undefined,
    displayName: player.displayName,
    firstName: player.firstName,
    lastName: player.lastName,
    shortLastName: player.shortLastName ?? player.lastName ?? null,
    matchName: player.matchName ?? null,
    position: mapStatsPosition(player.position),
    status: player.status ?? 'available',
    price: player.price,
    totalPoints: player.totalPoints,
    averagePoints: player.averagePoints,
    gameweekPoints: periodPoints,
    matchdayPoints: periodPoints,
    bonusPoints: player.bonusPoints,
    last3Average: player.last3Average,
    percentSelected: player.percentSelected,
    // The stats endpoint does not expose price movement.
    priceChange: 0,
    nextFixtures: player.nextGameweekFixtures?.map(
      (fixture): UiPlayerFixture => ({
        matchId: fixture.fixtureId,
        opponentId: fixture.opponentId,
        opponentName: fixture.opponentName,
        opponentShortName: fixture.opponentShortName,
        contestantFlagKey: fixture.contestantFlagKey,
        isHome: fixture.isHome,
        points: null,
        goalBonusNormalPoints: null,
        gameweek: fixture.gameweek,
        matchday: fixture.matchday,
        status: 'fixture',
        kickoffAt: fixture.kickoffAt,
        competitionName: fixture.competitionName,
      })
    ),
    suspensionDetails: player.suspensionDetails
      ? {
          type: player.suspensionDetails.type ?? '',
          startDate: player.suspensionDetails.startDate ?? '',
          endDate: player.suspensionDetails.endDate,
          matches: 0,
          description: player.suspensionDetails.description ?? '',
        }
      : null,
    injuryDetails: player.injuryDetails
      ? {
          type: player.injuryDetails.type ?? '',
          startDate: player.injuryDetails.startDate ?? '',
          endDate: player.injuryDetails.endDate,
          expectedEndDate: player.injuryDetails.expectedEndDate ?? '',
          status: player.injuryDetails.status ?? '',
        }
      : null,
  };
};
